import { useCallback, useEffect, useRef, useState } from "react";
import api, { API } from "../api/client";
import { toast } from "sonner";
import { Bell, Loader2, Check, Clock, X, ShieldAlert } from "lucide-react";
import logger from "../lib/logger";

/**
 * Listens for `digest_weekly` SSE events. Shows toast + browser notification
 * only when the latest digest is NOT yet acknowledged or snoozed by *this*
 * admin. Clicking the inline pill opens a modal with totals, SLA breaches,
 * and Ack / Snooze actions persisted via `/api/reports/digest/action`.
 */
export default function DigestNotifier() {
  const [digest, setDigest] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const announcedRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reports/weekly-digest");
      setDigest(data);
      maybeAnnounce(data);
    } catch (err) { logger.error(err); }
    // maybeAnnounce closes over component-level state but doesn't itself need
    // to be a dep — it's a stable plain function within this render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initial fetch + SSE subscription
  useEffect(() => {
    load();
    const es = new EventSource(`${API}/clock/override-stream`, { withCredentials: true });
    es.addEventListener("digest_weekly", (evt) => {
      try {
        const data = JSON.parse(evt.data);
        // Latest push doesn't include admin_state; refetch so we know if we've acked it
        setDigest((prev) => ({ ...data, admin_state: prev?.admin_state || null }));
        load();
      } catch (e) { logger.warn("digest parse:", e); }
    });
    es.addEventListener("password_reset_self_serve", (evt) => {
      try {
        const d = JSON.parse(evt.data);
        toast.info(
          `${d.user_name || d.user_email} reset their password via the self-serve flow`,
          { duration: 9000 },
        );
      } catch (e) { logger.warn("password_reset parse:", e); }
    });
    return () => es.close();
  }, [load]);

  function isLive(state) {
    if (!state) return true;  // never acked
    if (state.action === "ack") return false;
    if (state.action === "snooze" && state.snoozed_until) {
      return new Date(state.snoozed_until).getTime() <= Date.now();
    }
    return true;
  }

  function maybeAnnounce(d) {
    if (!d || announcedRef.current === d.digest_id) return;
    if (!isLive(d.admin_state)) return;
    announcedRef.current = d.digest_id;
    const t = d.totals;
    const i = d.incidents;
    const breaches = (d.sla_breaches || []).length;
    const summary = `${t.sign_ins} sign-ins · ${t.total_hours}h · ${i.overrides_pending} pending · ${breaches} SLA breach(es)`;
    toast.info(`Weekly digest — ${summary}`);
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      new Notification("Avidity — weekly digest", { body: summary, tag: `digest-${d.digest_id}` });
    }
  }

  const act = async (snoozeMinutes) => {
    if (!digest) return;
    setBusy(true);
    try {
      await api.post("/reports/digest/action", {
        digest_id: digest.digest_id,
        snooze_minutes: snoozeMinutes,
      });
      toast.success(snoozeMinutes ? `Snoozed ${snoozeMinutes} min` : "Acknowledged");
      await load();
      setOpen(false);
    } catch (err) {
      logger.error(err);
      toast.error("Failed");
    } finally {
      setBusy(false);
    }
  };

  const pushNow = async () => {
    try {
      await api.post("/reports/weekly-digest/push");
    } catch (err) { logger.error(err); }
  };

  if (!digest) {
    return (
      <button disabled className="px-3 py-1.5 border border-gray-300 text-xs rounded-sm flex items-center gap-1.5 opacity-60">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Digest
      </button>
    );
  }

  const live = isLive(digest.admin_state);
  const breaches = digest.sla_breaches || [];
  const t = digest.totals;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="digest-notifier-button"
        className={`px-3 py-1.5 border text-xs font-bold rounded-sm flex items-center gap-1.5 transition relative ${
          live
            ? "border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
            : "border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]"
        }`}
      >
        <Bell className="w-3.5 h-3.5" />
        Digest
        <span className="font-mono ml-1 text-gray-600">{t.sign_ins}·{t.total_hours}h</span>
        {live && <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-white border border-gray-300 max-w-lg w-full rounded-sm shadow-2xl"
            data-testid="digest-modal">
            <header className="flex items-center justify-between px-5 py-3 border-b">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Weekly compliance digest</div>
                <div className="font-black text-lg tracking-tight">Week of {digest.digest_id}</div>
              </div>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </header>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Kv k="Sign-ins" v={t.sign_ins} />
                <Kv k="Total hours" v={`${t.total_hours}h`} />
                <Kv k="Completions" v={t.cleaning_completions} />
                <Kv k="Photos attached" v={t.photos_attached} />
                <Kv k="Photos missing" v={t.photos_missing} red={t.photos_missing > 0} />
                <Kv k="Left fence" v={digest.incidents.left_geofence} red={digest.incidents.left_geofence > 0} />
                <Kv k="No-return" v={digest.incidents.left_without_return} red={digest.incidents.left_without_return > 0} />
                <Kv k="Pending overrides" v={digest.incidents.overrides_pending} red={digest.incidents.overrides_pending > 0} />
              </div>

              {breaches.length > 0 && (
                <div className="border-l-4 border-red-600 bg-red-50 px-4 py-3 rounded-sm" data-testid="digest-sla-banner">
                  <div className="flex items-center gap-2 text-red-800 font-bold text-sm">
                    <ShieldAlert className="w-4 h-4" /> {breaches.length} site photo SLA breach(es)
                  </div>
                  <ul className="mt-2 text-xs space-y-1">
                    {breaches.map((b) => (
                      <li key={b.site_id} className="font-mono">
                        <b>{b.site_name}</b> — {b.actual} / {b.target} photos
                        <span className="text-red-700"> (short by {b.short_by})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(digest.task_breaches || []).length > 0 && (
                <div className="border-l-4 border-amber-600 bg-amber-50 px-4 py-3 rounded-sm" data-testid="digest-task-banner">
                  <div className="flex items-center gap-2 text-amber-900 font-bold text-sm">
                    <ShieldAlert className="w-4 h-4" /> {digest.task_breaches.length} cleaning-task SLA breach(es)
                  </div>
                  <ul className="mt-2 text-xs space-y-1 max-h-[180px] overflow-y-auto">
                    {digest.task_breaches.map((b) => (
                      <li key={`${b.site_id}-${b.task_id}`} className="font-mono">
                        <b>{b.site_name}</b> · {b.task_label}
                        <span className="text-amber-900"> — {b.actual}/{b.target}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {digest.admin_state && (
                <div className="text-xs text-gray-500">
                  {digest.admin_state.action === "ack"
                    ? `Acknowledged ${new Date(digest.admin_state.created_at).toLocaleString()}`
                    : `Snoozed until ${digest.admin_state.snoozed_until ? new Date(digest.admin_state.snoozed_until).toLocaleString() : "—"}`}
                </div>
              )}
            </div>
            <footer className="border-t border-gray-200 p-3 flex flex-wrap gap-2">
              <button onClick={() => act(60)} disabled={busy}
                data-testid="digest-snooze-1h"
                className="border border-gray-300 px-3 py-2 rounded-sm text-xs font-bold hover:bg-gray-50 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Snooze 1h
              </button>
              <button onClick={() => act(24 * 60)} disabled={busy}
                data-testid="digest-snooze-1d"
                className="border border-gray-300 px-3 py-2 rounded-sm text-xs font-bold hover:bg-gray-50 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Snooze 1d
              </button>
              <button onClick={pushNow} disabled={busy}
                className="border border-gray-300 px-3 py-2 rounded-sm text-xs hover:bg-gray-50 ml-auto">
                Refresh
              </button>
              <button onClick={() => act(null)} disabled={busy}
                data-testid="digest-ack"
                className="bg-green-700 text-white px-4 py-2 rounded-sm text-xs font-bold hover:bg-green-800 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" /> Acknowledge
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

function Kv({ k, v, red }) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-1">
      <span className="text-gray-500">{k}</span>
      <span className={`font-mono font-bold ${red ? "text-red-700" : "text-gray-900"}`}>{v}</span>
    </div>
  );
}
